<template>
  <div
    class="f-column flex-1"
    :class="smallScreen ? 'mt-0' : 'my-auto'"
  >
    <div
      v-if="!paymentError || updatingPlan"
      class="f-column f-center"
    >
      <div
        v-if="!updatingPlan"
        class="user-message success-message mt-6"
      >
        <v-icon
          color="primary"
          class="user-message_icon"
          data-tst="update_success"
          >mdi-checkbox-marked-circle-outline</v-icon
        >
        <div
          v-if="$route.query.payment_provider === 'paypal'"
          class="user-message_note-box mt-2"
        >
          {{ $t('payment.paypal_receipt_note') }}
        </div>
        <div class="user-message_title mt-2">
          {{ $t('payment.your_plan_was_updated_to') }}
          <TierTag
            class="f-center mt-2"
            data-tst="payment_confirmation_tier"
            :customGame="customGame"
            :customTier="selectedPlan.tier"
            :paymentInterval="selectedPlan.interval"
          />

          <span
            v-if="selectedPlan.tier === 'PREMIUM'"
            style="font-size: 1rem"
          >
            {{ $t('payment.premium_boost_line') }}
          </span>
        </div>
      </div>

      <UpdatePaymentPlan
        v-if="updatingPlan"
        :startAtStep="3"
        style="text-align: center"
        class="mt-16"
      />

      <DiscordBox
        v-if="updatingPlan ? query.discord === 'true' : true"
        class="mt-0 discord-box mt-6"
      >
      </DiscordBox>
    </div>

    <!-- TODO: Do we really need this? I haven't found the case where we pass "error" to query params -->
    <div
      v-if="paymentError"
      class="f-column f-center mt-8"
    >
      <div class="user-message error-message">
        <v-icon
          color="var(--clr-error)"
          class="user-message_icon"
          >mdi-alert-circle-outline</v-icon
        >
        <div class="user-message_title mt-6">
          {{ $t('subscription.ups_wrong') }}
        </div>
      </div>
      <v-btn
        size="x-large"
        color="primary"
        class="mt-8 weight_700"
        @click="goBack"
        >{{ $t('btn.try_again') }}</v-btn
      >
      <div class="user-message_subtitle mt-8">
        {{ $t('subscription.or_get_help_on_our') }}
        <OpenChatLink />.
      </div>
    </div>

    <div class="clr-primary uppercase mb-16 weight_500 mt-16 flex-column">
      <div class="f-center">
        <router-link
          data-tst="link_to_dashboard"
          :to="{ name: 'Dashboard' }"
          class="link-underline link-underline_hover cursor-pointer"
          v-html="$t('subscription.continue_to_dashboard')"
        >
        </router-link>
        <v-icon class="clr-primary">mdi-chevron-right</v-icon>
      </div>
      <div class="f-center mt-4">
        <router-link
          data-tst="link_to_subscription"
          :to="{
            name: $store.getters.user_has_flag('REACT_SUBSCRIPTION_OVERVIEW')
              ? 'UserSubscriptionsOverview'
              : 'UserSubscription',
          }"
          class="link-underline link-underline_hover cursor-pointer clr-white"
          v-html="$t('subscription.subscription_overview')"
        >
        </router-link>
        <v-icon style="--clr-primary: var(--clr-white-0)">mdi-chevron-right</v-icon>
      </div>
    </div>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import DiscordBox from '@/components/Payments/DiscordBox'
import UpdatePaymentPlan from '@/components/Dialogs/UpdatePaymentPlanDialog/UpdatePaymentPlan'
import { seg_track, seg_getTotalAndCurrency, seg_getProduct } from '@/helpers/segment'
import { gtm_sub_thxpage } from '@/helpers/gtm'
import TierTag from '@/components/Sub/TierTag'
import payment_mix from '@/mixins/payment_mix'
import links_mix from '@/mixins/links_mix'
import OpenChatLink from '@/components/Payments/OpenChatLink.vue'

export default /** @type {any} */ {
  name: 'SubscriptionPaymentConfirmation',
  components: {
    OpenChatLink,
    TierTag,
    DiscordBox,
    UpdatePaymentPlan,
  },
  // name: 'SubscriptionPaymentUpdate', can also happen
  mixins: [payment_mix, links_mix],
  data() {
    return {}
  },
  created() {
    if (this.hasTrackDialog) {
      this.$store.dispatch('router_set_dialogs', 'how-did-you-dialog')
    }
  },
  mounted() {
    nextTick(() => {
      if (this.$route.name === 'SubscriptionPaymentConfirmation') {
        const { payment_provider } = this.$route.query
        const paymentSession = localStorage.getItem('gtm_payment_session')
        if (paymentSession) {
          localStorage.removeItem('gtm_payment_session')
          const plan = this.selectedPlan
          const transaction_id = plan.external_plans[payment_provider]?.external_plan_id
          gtm_sub_thxpage({
            currencyCode: 'USD',
            transaction_id,
            transaction_revenue: plan.price,
            game: this.customGame.toUpperCase(),
            type: plan.tier.toLowerCase(),
            paymentType: plan.interval.toLowerCase(),
            price: plan.price,
          })
        }

        const { payment_method } = this.$route.query
        if (payment_method && window.seg_checkout_id) {
          const product = {
            plan: this.selectedTierPlan?.plan,
            price: { price: this.selectedTierPlan?.plan?.price },
          }
          seg_track('purchase_completed', {
            step: 2,
            checkout_type: 'Purchase Funnel',
            checkout_id: window.seg_checkout_id,
            affiliation: this.$store.getters.user_info.affiliate_name,
            payment_method,
            ...seg_getTotalAndCurrency([product]),
            products: [seg_getProduct(product)],
            userId: this.$store.getters.user_info.public_id,
            email: this.$store.getters.user_info.email,
          })
        }
      }
    })
  },
  computed: {
    customGame() {
      return (this.query.gm_type || 'cash').toLowerCase()
    },
    query() {
      return this.$route.query
    },
    updatingPlan() {
      return this.query.update
    },
    hasTrackDialog() {
      return this.query.has_track_dialog
    },
    // TODO: Do we really need this? I haven't found the case where we pass "error" to query params
    paymentError() {
      const { error } = this.$route.query
      return error?.toLowerCase()
    },
    user_info() {
      const { user_info } = this.$store.getters
      return user_info
    },
    mdAndDown() {
      const { globals_mobile } = this.$store.getters
      return globals_mobile
    },
    smallScreen() {
      const { globals_window_size } = this.$store.getters
      return globals_window_size.width < 768
    },
  },
}
</script>

<style lang="scss" scoped>
.tier-tag {
  border-radius: 4px;
  font-weight: 700;
  // font-size: 1.25rem;
  flex: 0;
  &_trial {
    color: var(--clr-white-0);
  }
  &_starter {
    color: var(--clr-blue);
  }
  &_premium {
    color: var(--clr-yellow);
  }
  &_icon {
    font-size: 1.5rem;
    margin: 0 0.5rem 0 0;
    color: currentColor;
  }
}
.user-message {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  padding: 1rem;
  &_icon {
    font-size: 6rem !important;
  }
  &_title {
    font-size: 1.5rem;
    font-weight: 700;
    max-width: 42rem;
    padding: 0 1rem;
  }
  &_subtitle {
    font-size: 1rem;
    font-weight: 700;
  }
  &_note-box {
    max-width: 32rem;
    padding: 0 0.5rem;
    // text-align: left;
    text-align: center;
    font-size: 12px;
    color: var(--clr-grey-2);
  }
  &_text {
    font-size: 1.25rem;
    font-weight: 500;
  }
}

.discord-box {
  max-width: 21rem;
  margin: 0 1rem;
  background: var(--clr-black-1) !important;
}

.error-message {
  color: var(--clr-error);
}
</style>
